import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface CreateApiKeyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (name: string) => Promise<void>;
  isCreating: boolean;
}

const MAX_NAME_LENGTH = 60;

export function CreateApiKeyModal({
  open,
  onOpenChange,
  onCreate,
  isCreating,
}: CreateApiKeyModalProps) {
  useLanguage();
  const [name, setName] = useState('');

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !isCreating;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    // O nome só é limpo quando a criação some daqui (sucesso fecha o modal, e
    // o onOpenChange abaixo faz a limpeza). Se a chamada falhar — teto de
    // chaves atingido, rede caindo —, o modal continua aberto e o que o
    // usuário digitou continua lá para ele tentar de novo.
    await onCreate(trimmedName);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isCreating) {
          if (!next) setName('');
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="sm:max-w-[460px] rounded-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-foreground">
              {translateInterfaceText("Nova chave de API")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-1.5">
              {translateInterfaceText("Dê um nome que lembre onde a chave vai ser usada. É por ele que você vai saber qual revogar quando tiver mais de uma.")}</DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-2">
            <Label htmlFor="api-key-name" className="text-sm font-medium text-[#111]">
              {translateInterfaceText("Nome")}</Label>
            <Input
              id="api-key-name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, MAX_NAME_LENGTH))}
              placeholder={translateInterfaceText("Claude no notebook")}
              autoFocus
              className="h-[42px] rounded-[10px] border-[#E2E8F0]"
            />
            <p className="text-xs text-[#94A3B8]">
              {trimmedName.length}/{MAX_NAME_LENGTH}
            </p>
          </div>

          <DialogFooter className="mt-5 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isCreating}
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#1E293B]"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="h-10 px-4 rounded-[10px] bg-[#437DFF] hover:bg-[#355FCC] font-semibold"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {translateInterfaceText("Criando")}</>
              ) : (
                translateInterfaceText("Criar chave")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
